import * as React from 'react';

// import { z } from 'zod';
// import { useFormProgressUpdater } from '~/components/Form/FormProgress/FormProgress';
// import useFormState from '~/hooks/useFormState/useFormState';

function createFastContext<Store, Reducer>(
  initialState: Store,
  reducer: React.Reducer<Store, Reducer>,
) {
  function useStoreData(defaultValue?: Store) {
    const store = React.useRef(defaultValue ?? initialState);

    const get = React.useCallback(() => store.current, []);

    const subscribers = React.useRef(new Set<() => void>());

    const subscribe = React.useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => {
        subscribers.current.delete(callback);
      };
    }, []);

    const set = React.useCallback((newState: Reducer) => {
      store.current = reducer(store.current, newState);
      subscribers.current.forEach((callback) => callback());
    }, []);

    return { get, subscribe, set };
  }

  type UseStoreDataReturnType = ReturnType<typeof useStoreData>;

  const StoreContext = React.createContext<UseStoreDataReturnType | null>(null);

  function Provider({
    children,
    defaultValue,
  }: {
    children: React.ReactNode;
    defaultValue?: Store;
  }) {
    const store = useStoreData(defaultValue);
    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
  }

  function useStore<TData = Store>(
    selector: (store: Store) => TData,
  ): readonly [TData, (newState: Reducer) => void];

  function useStore(selector?: undefined): readonly [Store, (newState: Reducer) => void];

  function useStore<TData = Store>(
    selector?: (store: Store) => TData,
  ): readonly [Store | TData, (newState: Reducer) => void] {
    const store = React.useContext(StoreContext);

    if (!store) throw new Error('Store is not defined');
    const state = React.useSyncExternalStore(
      store.subscribe,
      () => (selector ? selector(store.get()) : store.get()),
      () => (selector ? selector(initialState) : initialState),
    );

    return [state, store.set] as const;
  }

  return { Provider, useStore };
}

export function createForm<TFormState, TFormSetState>(
  defaultState: TFormState,
  reducer: React.Reducer<TFormState, TFormSetState>,
) {
  const { useStore, Provider } = createFastContext(defaultState, reducer);

  function useFormValue<TFieldName extends keyof TFormState>(fieldName: TFieldName) {
    const c = useStore((store) => store[fieldName]);
    if (c == null) throw new Error('Context is not defined');
    return c;
  }

  function useAllFormData() {
    const c = useStore();
    if (c == null) throw new Error('Context is not defined');
    return c;
  }

  return {
    Provider,
    /**
     * useFormValue returns the value of a specific field in the form data and the dispatch function
     * use this for most of your form fields
     *
     * if you need access to multiple fields, you can use this twice or use useAllFormData
     * if you need access to the whole form data, use useAllFormData
     */
    useFormValue,
    /**
     * useAllFormData returns the whole form state and the dispatch function,
     * only use for submitting the form else you might get some questionable performance
     * (probably not a problem but still best to avoid it)
     */
    useAllFormData,
  };
}

// ! DO NOT REMOVE THIS CODE - LAST REVIEWED 17/1/24
// /**
//  * Example usage of above functions:
//  *
//  * A bit of setup done once per form,
//  * i recommend using zod to define the state type
//  * because you can then use it to validate the state
//  */

// const schema = z.object({
//   name: z.string(),
//   age: z.number(),
//   email: z.string().email(),
// });
// type State = z.infer<typeof schema>;

// const defaultState = {
//   name: '',
//   age: 0,
//   email: '',
// };

// // if you want to use a reducer, this is how i would do it,
// // but you can also use the object merge pattern (i'll create another example for that)
// const formDataActions = {
//   SET_FORM_VALUE: 'SET_FORM_VALUE',
//   REGISTER_FORM_FIELD: 'REGISTER_FORM_FIELD',
// } as const;

// type FormDataActions<TFormState, TFormName extends keyof TFormState> = {
//   type: (typeof formDataActions)[keyof typeof formDataActions];
//   name: TFormName;
//   value: TFormState[TFormName];
// };

// function exampleReducer(state: State, action: FormDataActions<State, keyof State>) {
//   switch (action.type) {
//     case formDataActions.SET_FORM_VALUE: {
//       return {
//         ...state,
//         [action.name]: action.value,
//       };
//     }
//     case formDataActions.REGISTER_FORM_FIELD: {
//       return {
//         ...state,
//         [action.name]: action.value,
//       };
//     }
//     default:
//       // should never happen as we're using ts to ensure the action.type is correct
//       throw new Error(`Unhandled action type: ${action.type}`);
//   }
// }

// // this is the meat of the example. how to create the Provider, useFormValue and useAllFormData
// // eslint-disable-next-line @typescript-eslint/no-unused-vars
// const { Provider, useFormValue, useAllFormData } = createForm(defaultState, exampleReducer);

// // you can add wrappers around the generated hooks to make them more convenient to use
// const useWrappedFormValue = <TFieldName extends keyof State>(
//   fieldName: TFieldName,
//   defaultValue?: State[TFieldName]
// ) => {
//   const [value, dispatch] = useFormValue(fieldName);
//   const fieldNameRef = React.useRef(fieldName);
//   const defaultValueRef = React.useRef(defaultValue);

//   React.useEffect(() => {
//     if (fieldName !== fieldNameRef.current) {
//       throw new Error(`fieldName must not change, old: ${fieldNameRef.current}, new: ${fieldName}`);
//     }
//   }, [fieldName]);

//   React.useEffect(() => {
//     if (defaultValue && defaultValue !== defaultValueRef.current) {
//       dispatch({
//         type: formDataActions.REGISTER_FORM_FIELD,
//         name: fieldName,
//         value: defaultValue,
//       });
//     }
//   }, [dispatch, fieldName, defaultValue]);

//   const setValue = React.useCallback(
//     (newValue: State[TFieldName]) => {
//       dispatch({ type: formDataActions.SET_FORM_VALUE, name: fieldName, value: newValue });
//     },
//     [dispatch, fieldName]
//   );
//   return [value, setValue] as const;
// };

// // and this is how it would be used in the component.
// // nice and clean, and nice autocomplete
// function Test() {
//   const [name, setFormValue] = useWrappedFormValue('name');
//   const [state, setState, onFocus] = useFormState(name);

//   return (
//     <div>
//       <input
//         value={state.value}
//         onChange={(e) => setState({ value: e.target.value })}
//         onFocus={onFocus}
//         onBlur={(e) => setFormValue(e.target.value)}
//       />
//     </div>
//   );
// }

// export function TestForm() {
//   return (
//     <Provider>
//       <Test />
//     </Provider>
//   );
// }

// /**
//  * another example reducer you could use is like this:
//  */

// const schema2 = z.object({
//   name: z.string(),
//   age: z.number(),
//   email: z.string().email(),
// });
// type State2 = z.infer<typeof schema2>;

// const defaultState2 = {
//   name: '',
//   age: 0,
//   email: '',
// };

// type Action = ((oldState: State2) => Partial<State2>) | Partial<State2>;

// function exampleReducer2(oldState: State2, action: Action) {
//   return {
//     ...oldState,
//     ...(typeof action === 'function' ? action(oldState) : action),
//   };
// }

// const {
//   Provider: Provider2,
//   useFormValue: useFormValue2,
//   useAllFormData: useAllFormData2,
// } = createForm(defaultState2, exampleReducer2);

// function Test2() {
//   const [name, setFormValue] = useFormValue2('name');
//   const [state, setState, onFocus] = useFormState(name);
//   const [formState, dispatch] = useAllFormData2();

//   return (
//     <div>
//       <input
//         value={state.value}
//         onChange={(e) => setState({ value: e.target.value })}
//         onFocus={onFocus}
//         onBlur={(e) => setFormValue({ name: e.target.value })}
//       />
//     </div>
//   );
// }

// export function TestForm2() {
//   return (
//     <Provider2>
//       <Test2 />
//     </Provider2>
//   );
// }

// /**
//  * another example reducer you could use is like this:
//  */

// const schema3 = z.object({
//   name: z.string().min(5, 'Name must be a minimum of 5 characters').optional(),
//   age: z.number(),
//   email: z.string().email(),
//   phone: z.string(),
// });
// type Schema3 = z.infer<typeof schema3>;

// type MappedSchema3<TSchema> = {
//   [K in keyof TSchema]: {
//     value?: TSchema[K];
//     error?: z.ZodError | null;
//     // eslint-disable-next-line @typescript-eslint/ban-types
//     required: TSchema[K] extends {} ? true : false;
//   };
// };

// type State3 = Required<MappedSchema3<Schema3>>;

// const defaultState3 = {
//   name: {
//     value: undefined,
//     error: null,
//     required: false,
//   },
//   age: {
//     value: undefined,
//     error: null,
//     required: true,
//   },
//   email: {
//     value: undefined,
//     error: null,
//     required: true,
//   },
//   phone: {
//     value: undefined,
//     error: null,
//     required: true,
//   },
// } satisfies State3;

// type Action3 = ((oldState: State3) => Partial<State3>) | Partial<State3>;

// function exampleReducer3(oldState: State3, action: Action3) {
//   return {
//     ...oldState,
//     ...(typeof action === 'function' ? action(oldState) : action),
//   };
// }

// const {
//   Provider: Provider3,
//   useFormValue: useFormValue3,
//   useAllFormData: useAllFormData3,
// } = createForm(defaultState3, exampleReducer3);

// type KeyOfState3 = Prettify<keyof State3>;

// function useWrappedFormValue3<FieldName extends KeyOfState3>(
//   fieldName: FieldName,
//   valueSchema: z.Schema<Schema3[FieldName]>
// ) {
//   type Value = State3[FieldName]['value'];
//   const [state, dispatch] = useFormValue3(fieldName);
//   const setProgress = useFormProgressUpdater(fieldName, defaultState3[fieldName].required);
//   const fieldNameRef = React.useRef(fieldName);

//   React.useEffect(() => {
//     if (fieldName !== fieldNameRef.current) {
//       throw new Error(`fieldName must not change, old: ${fieldNameRef.current}, new: ${fieldName}`);
//     }
//   }, [fieldName]);

//   const validator = React.useCallback(
//     (newValue: Value) => {
//       try {
//         const value = valueSchema.parse(newValue);
//         setProgress('valid');
//         return {
//           value,
//           error: null,
//         };
//       } catch (e) {
//         setProgress('invalid');
//         if (e instanceof z.ZodError) {
//           return {
//             value: undefined,
//             error: e,
//           };
//         }
//         throw e;
//       }
//     },
//     [valueSchema, setProgress]
//   );

//   const setValue = React.useCallback(
//     (newValue: Value) => {
//       const { value, error } = validator(newValue);
//       dispatch({
//         [fieldName]: {
//           value,
//           error,
//         },
//       });
//     },
//     [dispatch, fieldName, validator]
//   );
//   return [state, setValue, validator, setProgress] as const;
// }
// const formName = 'name';

// function Test3() {
//   // setProgress is done automatically in useWrappedFormValue3, but is exposed for custom use
//   const [name, setName, nameValidator, setProgress] = useWrappedFormValue3(
//     formName,
//     schema3.shape[formName]
//   );
//   const [state, setState, onFocus] = useFormState(name.value);
//   const [formState, dispatch] = useAllFormData3();

//   return (
//     <div>
//       <input
//         value={state.value}
//         onChange={(e) => {
//           const { value, error } = nameValidator(e.target.value);
//           setState({ value, error: !!error, errorMessage: error?.message });
//         }}
//         onFocus={onFocus}
//         onBlur={(e) => setName(e.target.value)}
//       />
//     </div>
//   );
// }

// export function TestForm3() {
//   return (
//     <Provider3>
//       <Test3 />
//     </Provider3>
//   );
// }
